import { useEffect, useRef, useState } from "react";
import { animate, motion } from "framer-motion";
import { FadeUp } from "@/components/Reveal";

const STATS = [
    { value: 30, suffix: "+", label: "Projects Delivered" },
    { value: 3, suffix: "+", label: "Years of Practice" },
    { value: 2, suffix: "", label: "Cities — Kollam | Kochi" },
];

function Counter({ value, suffix }) {
    const wrapperRef = useRef(null);
    const startedRef = useRef(false);
    const [display, setDisplay] = useState(0);
    const [done, setDone] = useState(false);
    const [visible, setVisible] = useState(false);

    // Robust in-view detection using IntersectionObserver.
    // A fallback timer guarantees the count runs even if the observer never
    // fires (e.g. reduced motion, tall viewport, etc.).
    useEffect(() => {
        if (typeof IntersectionObserver === "undefined") {
            setVisible(true);
            return;
        }
        const el = wrapperRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.2, rootMargin: "0px 0px -10% 0px" },
        );
        observer.observe(el);
        // Fallback — animate after 2.5s no matter what
        const fallback = setTimeout(() => setVisible(true), 2500);
        return () => {
            observer.disconnect();
            clearTimeout(fallback);
        };
    }, []);

    useEffect(() => {
        if (!visible || startedRef.current) return;
        startedRef.current = true;
        const controls = animate(0, value, {
            duration: 1.6,
            ease: [0.16, 1, 0.3, 1],
            onUpdate: (v) => setDisplay(Math.round(v)),
            onComplete: () => setDone(true),
        });
        return () => controls.stop();
    }, [visible, value]);

    return (
        <span
            ref={wrapperRef}
            className="relative inline-block tabular-nums"
        >
            <motion.span
                className="pointer-events-none absolute inset-0 rounded-lg bg-gold/25 blur-2xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: visible ? [0, 0.85, 0] : 0 }}
                transition={{ duration: 2.2, ease: "easeOut" }}
                style={{ zIndex: -1 }}
                aria-hidden="true"
            />
            <motion.span
                className="relative inline-block"
                animate={done ? { scale: [1, 1.08, 1] } : { scale: 1 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
                {display}
                <span className="text-gold">{suffix}</span>
            </motion.span>
        </span>
    );
}

export default function Stats() {
    return (
        <section
            id="numbers"
            data-testid="stats-section"
            className="mx-auto max-w-[1500px] px-6 py-32 sm:px-10"
        >
            <div className="grid grid-cols-1 border border-white/[0.07] sm:grid-cols-2 lg:grid-cols-3">
                {STATS.map((s, i) => (
                    <motion.div
                        key={s.label}
                        data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
                        className="relative overflow-hidden border-white/[0.07] bg-onyx p-10 [&:not(:last-child)]:border-b sm:[&:nth-child(odd)]:border-r lg:[&:not(:last-child)]:border-r lg:[&:not(:last-child)]:border-b-0"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-12%" }}
                        transition={{
                            duration: 0.9,
                            delay: i * 0.12,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                    >
                        <span className="absolute left-0 top-0 h-1 w-10 bg-gold/70" />
                        <p className="font-display text-6xl font-black tracking-tight text-white sm:text-7xl">
                            <Counter value={s.value} suffix={s.suffix} />
                        </p>
                        <p className="mt-4 text-[11px] font-medium uppercase tracking-[0.35em] text-ash">
                            {s.label}
                        </p>
                    </motion.div>
                ))}
            </div>

            <FadeUp delay={0.2} className="mt-8">
                <p className="text-xs uppercase tracking-[0.3em] text-ash/70">
                    Building across Kerala since 2023
                </p>
            </FadeUp>
        </section>
    );
}
